import {Component, OnInit, signal, inject, DestroyRef} from '@angular/core';
import {BreadcrumbItem} from '../../models/breadcrumb/breadcrumb-item';
import {translate} from '@jsverse/transloco';
import {AuthService} from '../../services/auth/auth.service';
import {ActivatedRoute, Router} from '@angular/router';
import {RouterSegments} from '../../models/router-segments';
import {BehaviorSubject, Subscription, combineLatest, forkJoin, of} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {SolutionsService} from '../../services/api/solutions.service';
import {Solution} from '../../models/service/solution';
import {KeyValueMap} from '../../models/key-value-map';
import {LineOfBusiness} from '../../models/line-of-business';
import {DropdownFilterGroup} from '../../models/filter/dropdown-filter';
import {catchError, finalize, map, mergeMap} from 'rxjs/operators';
import {SolutionVersion} from '../../models/service/solution-version';
import {ServiceCardService} from '../../services/util/service-card.service';
import {ServiceCardInfo} from '../../models/service/service-card-info';
import {SandboxStatus, ServiceStatus} from '../../models/service/service-status';
import {VerticalMenuItem} from '../../models/vertical-menu/vertical-menu-item';
import {Permission} from '../../models/principal/permission';
import {TenantsService} from '../../services/api/tenants.service';
import {DeployStatus} from '../../models/deploy/deploy-status';
import {TrialStatusService} from 'src/app/services/util/trial-status.service';
import { PermissionsService } from 'src/app/services/util/permissions.service';
import { BlueprintsService } from 'src/app/services/api/blueprints.service';
import { Title } from '@angular/platform-browser';
import { ToastrService } from 'src/app/services/util/toastr.service';
import { PermissionKeys } from 'src/app/models/principal/permission-keys';
import { Permissions } from 'src/app/models/principal/permission';

const UPDATED_REQUIRED_PERMISSIONS = [
  {
    key: PermissionKeys.BLUEPRINT_READ,
    action: Permissions.BLUEPRINT_READ
  },
  {
    key: PermissionKeys.BLUEPRINT_CREATE,
    action: Permissions.BLUEPRINT_CREATE
  }
];


const REQUIRED_PERMISSIONS = [
  Permission.SERVICES_SECTION,
  Permission.NO_SERVICES
];

type FilterFunc = (cardInfo: ServiceCardInfo, filter: string) => boolean;
@Component({
    selector: 'app-view-my-services',
    templateUrl: './view-my-services.component.html',
    styleUrls: ['./view-my-services.component.scss'],
    standalone: false
})
export class ViewMyServicesComponent implements OnInit {
private destroyRef = inject(DestroyRef);
  breadcrumbItems: BreadcrumbItem[];
  RouterSegments = RouterSegments;
  verticalMenuItems: VerticalMenuItem[];
  selectedMenuItem: VerticalMenuItem;

  isGridView = true;
  isListView = false;
  isServices = true;
  isAllServices = true;
  isLiveServices = false;
  isInDevelopmentServices = false;
  isSandboxes = false;
  isAllSandboxes = false;
  isActiveSandboxes = false;
  isInProgressSandboxes = false;
  isExpiredSandboxes = false;
  solutions: Solution[] = [];
  lineOfBusinessMap: KeyValueMap<string, LineOfBusiness> = {};
  lineOfBusinesses: LineOfBusiness[];
  filterGroups: DropdownFilterGroup[];
  servicesVersionsMap = new Map<string, SolutionVersion[]>();
  sandboxCardInfos: ServiceCardInfo[];
  serviceCardInfos: ServiceCardInfo[];
  filteredServiceCardInfos: ServiceCardInfo[] = [];
  filteredSandboxCardInfos: ServiceCardInfo[] = [];
  categoryFilters: any;
  statusFilters: any;
  filter: string;
  FILTERS: KeyValueMap<string, FilterFunc> = {
    [ServiceStatus.IN_PROGRESS]: this.isInServiceStatus,
    [ServiceStatus.LIVE]: this.isInServiceStatus
  };
  tenantDetails: any;
  activeMenu = 'ALL';
  enterpriseClient: boolean;
  lineOfBusinessesFilterGroups: DropdownFilterGroup[] = [];
  statusFilterGroups: DropdownFilterGroup[] = [];
  permissionsMap: {[key: string]: boolean};
  Permission = Permission;
  dataLoading = new BehaviorSubject<boolean>(true);
  filterSubscription: Subscription;
  isLoading = true;
  blueprintsData: any;
  loader = true;
  tenant: string;
  updatedPermissionsMap: { [key: string]: boolean };
  PermissionKeys = PermissionKeys;

  constructor(
    private authService: AuthService,
    private router: Router,
    private readonly route: ActivatedRoute,
    private serviceCardService: ServiceCardService,
    private solutionsService: SolutionsService,
    private tenantsService: TenantsService,
    private trialStatusService: TrialStatusService,
    private permissionsService: PermissionsService,
    private blueprintsService: BlueprintsService,
    private readonly titleService: Title, private toastrService: ToastrService) {
  }

  ngOnInit(): void {
    this.titleService.setTitle(this.route.snapshot.data.title);
    this.tenant = this.authService.principal.provider;
    this.updatedPermissionsMap = this.permissionsService.resolveAPIPermissions(UPDATED_REQUIRED_PERMISSIONS);
    if (!this.updatedPermissionsMap[PermissionKeys.BLUEPRINT_READ]) {
      this.router.navigate(['/', this.tenant, RouterSegments.UNAUTHORIZED], {skipLocationChange: true});
    }
    this.permissionsMap = this.permissionsService.resolveUserPermissions(REQUIRED_PERMISSIONS);
    this.tenantsService.getTenantDetails()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(tenantData => {
        this.tenantDetails = tenantData;
      });
    this.isServices = history.state.data ? !history.state.data : this.permissionsMap[Permission.NO_SERVICES] ? false :  true;
    this.dataLoading.next(false);
    this.configureVerticalMenu();
    this.loadData();
  }

  goToComposeService(): void {
    this.router.navigate(['/', this.authService.principal.provider, RouterSegments.COMPOSE]);
  }

  // TODO: this function need to be revisited when my service UX is updated
  gotoSection(menuItem: VerticalMenuItem): void {
    this.selectedMenuItem = menuItem;
    this.activeMenu = menuItem.name;
    this.loadSection(menuItem.nameKey);
  }

  loadSection(selectedMenu: string): void {
    if (selectedMenu.includes('MY_SERVICES.SIDE_MENU.SERVICES')) {
      if (this.activeMenu === 'ALL') {
        this.isAllServices = true;
        this.isLiveServices = false;
        this.isInDevelopmentServices = false;
      } else if (this.activeMenu === 'LIVE') {
        this.isAllServices = false;
        this.isLiveServices = true;
        this.isInDevelopmentServices = false;
      } else if (this.activeMenu === 'IN_PROGRESS') {
        this.isAllServices = false;
        this.isLiveServices = false;
        this.isInDevelopmentServices = true;
      }
      this.isServices = true;
      this.isSandboxes = false;
      this.isAllSandboxes = false;
      this.isActiveSandboxes = false;
      this.isInProgressSandboxes = false;
      this.isExpiredSandboxes = false;
      this.applyServicesFilter(this.activeMenu);
    } else if (selectedMenu.includes('MY_SERVICES.SIDE_MENU.SANDBOXES')) {
      if (this.activeMenu === 'ALL') {
        this.isAllSandboxes = true;
        this.isActiveSandboxes = false;
        this.isInProgressSandboxes = false;
        this.isExpiredSandboxes = false;
      } else if (this.activeMenu === 'ACTIVE') {
        this.isAllSandboxes = false;
        this.isActiveSandboxes = true;
        this.isInProgressSandboxes = false;
        this.isExpiredSandboxes = false;
      } else if (this.activeMenu === 'IN_PROGRESS') {
        this.isAllSandboxes = false;
        this.isActiveSandboxes = false;
        this.isInProgressSandboxes = true;
        this.isExpiredSandboxes = false;
      } else if (this.activeMenu === 'EXPIRED') {
        this.isAllSandboxes = false;
        this.isActiveSandboxes = false;
        this.isInProgressSandboxes = false;
        this.isExpiredSandboxes = true;
      }
      this.isSandboxes = true;
      this.isServices = false;
      this.isAllServices = false;
      this.isLiveServices = false;
      this.isInDevelopmentServices = false;
      this.applySandboxFilter(this.activeMenu);
    }
    this.loadDropdownFilterCategory(this.categoryFilters);
  }

  switchView(type: string): void {
    if (type === 'grid') {
      this.isGridView = true;
      this.isListView = false;
    } else if (type === 'list') {
      this.isListView = true;
      this.isGridView = false;
    }
  }

  loadDropdownFilterCategory(filters: any): void {
    this.categoryFilters = filters;
    this.applyServicesFilter(this.activeMenu);
    this.applySandboxFilter(this.activeMenu);
    const sampleCardInfos = this.isServices ? this.filteredServiceCardInfos : this.filteredSandboxCardInfos;
    if (this.categoryFilters) {
      const cardInfos = [];
      sampleCardInfos?.forEach(serviceCardInfos =>
        this.categoryFilters?.forEach( filter =>
          {
            if (serviceCardInfos?.service?.lineOfBusiness[0] === filter) {
              cardInfos.push(serviceCardInfos);
            }
          }
        )
      );
      if (this.isServices) {
        this.filteredServiceCardInfos = cardInfos;
      } else {
        this.filteredSandboxCardInfos = cardInfos;
      }
    } else {
      this.applyServicesFilter(this.activeMenu);
      this.applySandboxFilter(this.activeMenu);
    }
  }

  loadDropdownFilterStatus(filters: any): void {
    this.statusFilters = filters?.status;
    const sampleCardInfos = this.filteredSandboxCardInfos;
    if (this.statusFilters) {
      const cardInfos = [];
      sampleCardInfos?.forEach(sandboxCardInfos =>
        this.statusFilters?.forEach( filter =>
          {
            if (filter === translate('SERVICE_STATUS.ACTIVE' ) && sandboxCardInfos.instances[0].status === DeployStatus.DEPLOYED) {
              cardInfos.push(sandboxCardInfos);
            }
            if (filter === translate('SERVICE_STATUS.IN_PROGRESS' ) && sandboxCardInfos.instances[0].status === DeployStatus.DEPLOYING) {
              cardInfos.push(sandboxCardInfos);
            }
            if (filter === translate('SERVICE_STATUS.EXPIRED' ) && sandboxCardInfos.instances[0].status === DeployStatus.TERMINATED) {
              cardInfos.push(sandboxCardInfos);
            }
          }
        ));
      this.filteredSandboxCardInfos = cardInfos;
    } else {
      this.applySandboxFilter(this.activeMenu);
    }
  }

  private loadData(): void {
    combineLatest([this.solutionsService.getSolutions(), this.solutionsService.getLineOfBusinesses()])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([solutions, lineOfBusinesses]) => {
        this.solutions = [...solutions];
        lineOfBusinesses.forEach(lineOfBusiness => {
          this.lineOfBusinessMap[lineOfBusiness.name] = lineOfBusiness;
        });
        this.lineOfBusinesses = lineOfBusinesses;
        this.configureBreadcrumb();
        this.loadSolutions();
        this.configureFilterGroup(lineOfBusinesses);
      });
  }

  private configureFilterGroup(filterList: any): void {
    const lineOfBusinessesFilterGroupItem: any = [];
    lineOfBusinessesFilterGroupItem.items = [];
    this.lineOfBusinesses.forEach(item => {
      const lineItem: any = [];
      lineItem.name = item.name;
      lineItem.displayName = item.displayName;
      lineItem.selected = false;
      lineOfBusinessesFilterGroupItem.name = 'category';
      lineOfBusinessesFilterGroupItem.items.push(lineItem);
    });
    this.lineOfBusinessesFilterGroups.push(lineOfBusinessesFilterGroupItem);

    this.statusFilterGroups = [{
      name: 'status',
      items: [
        { name: translate('SERVICE_STATUS.ACTIVE'), selected: false},
        { name: translate('SERVICE_STATUS.IN_PROGRESS'), selected: false},
        { name: translate('SERVICE_STATUS.EXPIRED'), selected: false}
      ]}
    ];
  }

  /**
   * Loads the solutions and for each solution loads its versions and prepares the Map
   * containing the needed information for the cards
   */
  private loadSolutions(): void {
    this.blueprintsService.getBlueprints().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(response => {
      this.blueprintsData = response;
      this.filteredServiceCardInfos = this.blueprintsData.filter(item => item.demo === false);
      this.filteredServiceCardInfos.sort(this.defaultComparator);
      this.filteredSandboxCardInfos = this.blueprintsData.filter(item => item.demo === true);
      this.filteredSandboxCardInfos.sort(this.defaultComparator);
      this.isLoading = false;
    });
    this.solutionsService.getMySolutions('myServices')
    .pipe(
      mergeMap((solutions: any[]) => forkJoin(
        solutions.map(solution => this.solutionsService.getSolutionVersions(solution.id)
          .pipe(
            map((versions: any) => {
              this.servicesVersionsMap.set(solution.id, versions);
              return solution;
            }),
            catchError(error => of(error))
          ))
        )
      ),
      takeUntilDestroyed(this.destroyRef)
    )
    .subscribe(solutions => {
      this.loadServiceCardInformation(solutions);
    });
  }


  /**
   * Loads the information needed for displaying service cards
   */
  private loadServiceCardInformation(services: Solution[]): void {
    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }
    const allCards = `${translate('SERVICE.ALL_CARDS')}`;
    this.serviceCardService.setCards('typeOfCard', allCards);
    // return;
    this.serviceCardService.getServiceCardInfos(services, this.servicesVersionsMap)
      .pipe(
        finalize(() => {
          this.filterSubscription = undefined;
      }),takeUntilDestroyed(this.destroyRef))
      .subscribe( async cardInfos => {
        const promises = [];
        this.sandboxCardInfos = cardInfos.filter(cardInfo => cardInfo.blueprint.demo && cardInfo.instances?.length);
        this.sandboxCardInfos.forEach( card => {
          const latestVersion = this.getLatestVersion(card);
          promises.push(latestVersion);
        });
         Promise.allSettled(promises).then(latestVersions => {
          latestVersions = latestVersions.map((version: any) => version.value);
          this.sandboxCardInfos = this.sandboxCardInfos.filter(cardInfo => cardInfo.blueprint.demo);
          this.sandboxCardInfos.forEach((card: any) => {
            card.latestVersion = latestVersions.find((version: any) => version?.blueprintId === card.blueprint?.id);
            if (!card.sandboxCardInfos?.devVersion) {
              if(this.servicesVersionsMap.get(card.blueprint.solutionId)) {
                card.buildNumber = this.servicesVersionsMap.get(card.blueprint.solutionId)[0]?.version;
              }
            }
          });
        });

        this.serviceCardInfos = cardInfos.filter(cardInfo => !cardInfo.blueprint.demo);

        this.serviceCardInfos.forEach((card: any) => {
          if (!card.serviceVersionsInfo?.devVersion) {
            if(this.servicesVersionsMap.get(card.blueprint.solutionId)) {
              card.buildNumber = this.servicesVersionsMap.get(card.blueprint.solutionId)[0]?.version;
            }
          }
        });

        this.filteredServiceCardInfos = this.serviceCardInfos;
        this.filteredServiceCardInfos.sort(this.defaultComparator);
        this.filteredSandboxCardInfos = this.sandboxCardInfos;
        this.filteredSandboxCardInfos.sort(this.defaultComparator);
        this.loader = false;
        if(!this.loader) {
          this.verticalMenuItems.forEach(item => {
            item.disabled = false;
            if(this.permissionsMap[Permission.NO_SERVICES]) {
              item.expanded = true;
            }
            if (item.nameKey === 'MY_SERVICES.SERVICES') {
              item.expanded = true;
            }
          });
        }
        this.applyServicesFilter(this.activeMenu);
        this.applySandboxFilter(this.activeMenu);
        this.dataLoading.next(false);

      });
  }

  private getLatestVersion(card): Promise<any> {
    return new Promise((resolve) => {
      this.blueprintsService.getBlueprintsIdLatestVersion(card.blueprint.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        resolve(result);
      }, (error) => {
        resolve(error);
        this.toastrService.clear();
            setTimeout(() => {
		  const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
      });
    });
  }

  private configureBreadcrumb(): void {
    this.breadcrumbItems = [];
    this.breadcrumbItems.push({label: translate('HOME.HOME'), url:[]});
    this.breadcrumbItems.push({label: translate('MY_SERVICES.TITLE')});
  }

  private configureVerticalMenu(): void {
    this.enterpriseClient = this.authService.hasPermission(Permission.VIEW_ENTERPRISE_SERVICE);
    const sandboxMenuSelection = !this.enterpriseClient || !this.isServices || this.permissionsMap[Permission.NO_SERVICES];
    const serviceMenuItems = {
      nameKey: 'MY_SERVICES.SERVICES',
      name: 'SERVICES',
      img: '/assets/icons/bank.svg',
      expanded: false,
      disabled: true,
      items: [
        {
          nameKey: 'MY_SERVICES.SIDE_MENU.SERVICES.ALL',
          name: 'ALL',
          color: 'primary',
          selected: !sandboxMenuSelection,
        },
        {
          nameKey: 'MY_SERVICES.SIDE_MENU.SERVICES.LIVE',
          name: 'LIVE',
          color: 'green',
          selected: false,
        },
        {
          nameKey: 'MY_SERVICES.SIDE_MENU.SERVICES.IN_DEVELOPMENT',
          name: 'IN_PROGRESS',
          color: 'purple-2',
          selected: false,
        },
      ]
    };

    const sandboxMenuItems = {
      nameKey: 'MY_SERVICES.SANDBOXES',
      name: 'SANDBOXES',
      img: '/assets/images/sandbox.png',
      expanded: this.permissionsMap[Permission.NO_SERVICES] ? false : sandboxMenuSelection,
      disabled: true,
      items: [
        {
          nameKey: 'MY_SERVICES.SIDE_MENU.SANDBOXES.ALL',
          name: 'ALL',
          color: 'primary',
          selected: sandboxMenuSelection,
        },
        {
          nameKey: 'MY_SERVICES.SIDE_MENU.SANDBOXES.ACTIVE',
          name: 'ACTIVE',
          color: 'green',
          selected: false,
        },
        {
          nameKey: 'MY_SERVICES.SIDE_MENU.SANDBOXES.IN_PROGRESS',
          name: 'IN_PROGRESS',
          color: 'purple-2',
          selected: false,
        },
        /* Will be added when we have expired status in response
        {
          nameKey: 'MY_SERVICES.SIDE_MENU.SANDBOXES.EXPIRED',
          name: 'EXPIRED',
          color: 'purple-2',
          selected: false,
        },*/
      ]
    };
    if(this.enterpriseClient && this.isServices) {
      this.verticalMenuItems = [serviceMenuItems];
      // if(!this.permissionsMap[Permission.SERVICES_SECTION] || this.permissionsMap[Permission.NO_SERVICES]){
      //   this.verticalMenuItems = [sandboxMenuItems];
      // }else{
      //   this.verticalMenuItems = [serviceMenuItems, sandboxMenuItems];
      // }
      this.loadSection('MY_SERVICES.SIDE_MENU.SERVICES.ALL');
    } else if(this.enterpriseClient && !this.isServices) {
      this.verticalMenuItems = [serviceMenuItems];
      // if(!this.permissionsMap[Permission.SERVICES_SECTION] || this.permissionsMap[Permission.NO_SERVICES]){
      //   this.verticalMenuItems = [sandboxMenuItems];
      // }else{
      //   this.verticalMenuItems = [serviceMenuItems, sandboxMenuItems];
      // }
      this.loadSection('MY_SERVICES.SIDE_MENU.SANDBOXES.ALL');
    } else {
      // this.verticalMenuItems = [sandboxMenuItems];
      // this.activeMenu = translate('MY_SERVICES.SIDE_MENU.SANDBOXES.ALL');
      // this.loadSection('MY_SERVICES.SIDE_MENU.SANDBOXES.ALL');
    }
  }

  private applyServicesFilter(selectedMenu: string): void {
    if(this.serviceCardInfos) {
      if (this.activeMenu.toUpperCase() === translate('MY_SERVICES.SIDE_MENU.SERVICES.ALL').toUpperCase()) {
        this.filteredServiceCardInfos = this.serviceCardInfos;
      } else {
        this.filteredServiceCardInfos = this.serviceCardInfos?.filter(serviceCardInfos =>
          this.isInServiceStatus(serviceCardInfos, selectedMenu)
        );
      }
      this.filteredServiceCardInfos.sort(this.defaultComparator);
    }
  }

  private applySandboxFilter(selectedMenu: string): void {
    if(this.sandboxCardInfos) {
      if (this.activeMenu.toUpperCase() === translate('MY_SERVICES.SIDE_MENU.SANDBOXES.ALL').toUpperCase()) {
        this.filteredSandboxCardInfos = this.sandboxCardInfos;
      } else {
        this.filteredSandboxCardInfos = this.sandboxCardInfos?.filter(sandboxCardInfos =>
          this.isInSandboxesStatus(sandboxCardInfos, selectedMenu)
        );
      }
      this.filteredSandboxCardInfos.sort(this.defaultComparator);
    }
  }

  private isInServiceStatus(cardInfo: ServiceCardInfo, filter: string): boolean {
    return cardInfo?.status === filter.toUpperCase();
  }

  private isInSandboxesStatus(cardInfo: ServiceCardInfo, filter: string): boolean {
    if (filter === SandboxStatus.ACTIVE
      && cardInfo.instances[0].status === DeployStatus.DEPLOYED
      && this.trialStatusService.trailExpireDays(cardInfo.instances[0].expirationDate) > 0) {
      return true;
    }
    if (filter === SandboxStatus.IN_PROGRESS && cardInfo.instances[0].status === DeployStatus.DEPLOYING) {
      return true;
    }
  }

  private defaultComparator(config1: any, config2: any): number {
  if(config1?.blueprint) {
    if (config1?.blueprint.dateCreated && config2?.blueprint.dateCreated) {
      return new Date(config2.blueprint.dateCreated).getTime() - new Date(config1.blueprint.dateCreated).getTime();
    }
    return 0;
    } else {
      if (config1?.dateCreated && config2?.dateCreated) {
        return new Date(config2.dateCreated).getTime() - new Date(config1.dateCreated).getTime();
      }
      return 0;
    }
  }
}
